import SKILL_REACH from "../ui/skillreach.json";
import { hasAffect } from "../render/effectsanchor";

/** One skill's `skilldesc.txt` attribute tokens and `skilltable.txt` target
 *  range, as `tools/import_skilldesc.py` reads them from the 2014 client. */
interface ReachRow {
  readonly attrs: readonly string[];
  readonly range: number;
}

const ROWS: Readonly<Record<string, ReachRow | undefined>> = SKILL_REACH;

/** `MELEE_SKILL_TARGET_RANGE` (`PythonSkill.cpp:1051`), in legacy units. */
export const MELEE_SKILL_TARGET_RANGE = 170;
/** A legacy unit is a centimetre; this client's positions are millimetres. */
const MM_PER_UNIT = 10;
/** `IS_HUGE_RACE`'s only race (`ActorInstanceBattle.cpp:549`). */
export const HUGE_RACE_VNUM = 2493;
/** What a huge race's distance is shortened by (`PythonPlayerSkill.cpp:407-410`). */
const HUGE_RACE_SLACK = 200;

function has(vnum: number, attr: string): boolean {
  return ROWS[String(vnum)]?.attrs.includes(attr) ?? false;
}

/** `IsChargeSkill` (`PythonSkill.cpp:1071-1077`). */
export function isChargeSkill(vnum: number): boolean {
  return has(vnum, "CHARGE_ATTACK");
}

/** `__GetSkillTargetRange` (`PythonPlayerSkill.cpp:391-394`) over
 *  `GetTargetRange` (`PythonSkill.cpp:1087-1096`): a melee or charge skill
 *  reaches `MELEE_SKILL_TARGET_RANGE`, anything else its table range, plus a
 *  hundred per point of bow distance. Legacy units. */
export function skillTargetRange(vnum: number, bowDistance: number): number {
  const row = ROWS[String(vnum)];
  const base = has(vnum, "MELEE_ATTACK") || has(vnum, "CHARGE_ATTACK")
    ? MELEE_SKILL_TARGET_RANGE
    : (row?.range ?? 0);
  return base + bowDistance * 100;
}

/** `__ReserveUseSkill`'s slack (`PythonPlayerInput.cpp:862-863`): a range over
 *  a hundred is reserved ten short, so the walk does not stop on the edge. */
export function reservedRange(range: number): number {
  return range > 100 ? range - 10 : range;
}

/** The distance `__UseSkill` compares, in legacy units: centre to centre, less
 *  two metres against a huge race (`PythonPlayerSkill.cpp:403-411`,
 *  `PythonPlayerInputMouse.cpp:346-354`). */
export function skillDistance(
  selfXMm: number, selfYMm: number,
  target: { readonly xMm: number; readonly yMm: number; readonly mobVnum: number },
): number {
  const units = Math.hypot(target.xMm - selfXMm, target.yMm - selfYMm) / MM_PER_UNIT;
  return target.mobVnum === HUGE_RACE_VNUM ? units - HUGE_RACE_SLACK : units;
}

export interface SkillReachInput {
  readonly vnum: number;
  readonly selfId: number;
  readonly selfXMm: number;
  readonly selfYMm: number;
  /** The selected body the cast names, or null for none. */
  readonly target: {
    readonly id: number;
    readonly kind: number;
    readonly xMm: number;
    readonly yMm: number;
    readonly mobVnum: number;
  } | null;
  /** `IsAttackableInstance(target)`. */
  readonly attackable: boolean;
  /** Whether the target is a player character (`IsPC`). */
  readonly targetIsPlayer: boolean;
  /** `POINT_BOW_DISTANCE`. */
  readonly bowDistance: number;
  /** The skill a walk is already reserved for, or null (`__IsReservedUseSkill`). */
  readonly reservedVnum: number | null;
}

/** What `__UseSkill` does with a cast before it sends one. */
export type SkillReach =
  /** Cast now, where the caster stands. */
  | { readonly kind: "cast" }
  /** Out of range: reserve the cast and walk to `entityId` until the distance
   *  falls under `rangeUnits`. `chargeStart` first sends the skill with no
   *  target, the charge's opening packet. */
  | {
    readonly kind: "approach";
    readonly entityId: number;
    readonly rangeUnits: number;
    readonly chargeStart: boolean;
  };

const CAST: SkillReach = { kind: "cast" };

/** `CPythonPlayer::__UseSkill`'s range arms (`PythonPlayerSkill.cpp:520-587`)
 *  and `__ProcessEnemySkillTargetRange` (`PythonPlayerSkill.cpp:396-437`). A
 *  skill that neither needs a target, turns to one nor searches for one is
 *  cast in place (`PythonPlayerSkill.cpp:522-524`). An alliance skill walks
 *  only to a friendly player out of range (`:555-568`); an enemy skill walks
 *  to an attackable target at or past its range, and a charge sends its
 *  opening packet with no target the first time (`:412-420`). Every other
 *  refusal those arms make is the server's to make. */
export function skillReach(input: SkillReachInput): SkillReach {
  const { vnum, target } = input;
  if (!has(vnum, "NEED_TARGET") && !has(vnum, "CAN_CHANGE_DIRECTION") && !has(vnum, "SEARCH_TARGET")) {
    return CAST;
  }
  if (target === null || target.id === input.selfId) return CAST;
  const range = skillTargetRange(vnum, input.bowDistance);
  if (range <= 0) return CAST; // PythonPlayerSkill.cpp:400-401, :559
  const distance = has(vnum, "ONLY_FOR_ALLIANCE")
    ? Math.hypot(target.xMm - input.selfXMm, target.yMm - input.selfYMm) / MM_PER_UNIT // PythonPlayerSkill.cpp:562
    : skillDistance(input.selfXMm, input.selfYMm, target);
  if (distance < range) return CAST; // PythonPlayerSkill.cpp:413, :563
  if (has(vnum, "ONLY_FOR_ALLIANCE")) {
    if (input.attackable || !input.targetIsPlayer) return CAST; // PythonPlayerSkill.cpp:555
    return { kind: "approach", entityId: target.id, rangeUnits: reservedRange(range), chargeStart: false };
  }
  if (!input.attackable) return CAST; // PythonPlayerSkill.cpp:580-586
  return {
    kind: "approach",
    entityId: target.id,
    rangeUnits: reservedRange(range),
    chargeStart: isChargeSkill(vnum) && input.reservedVnum !== vnum, // PythonPlayerSkill.cpp:414-418
  };
}

/** `MODE_USE_SKILL`'s per-frame arm (`PythonPlayerInputMouse.cpp:342-373`):
 *  once under the reserved range, retarget and cast; until then, keep walking
 *  at the body; gone, forget the reservation. */
export function reservedSkillStep(
  selfXMm: number, selfYMm: number,
  quarry: { readonly xMm: number; readonly yMm: number; readonly mobVnum: number } | null,
  rangeUnits: number,
): "drop" | "cast" | "follow" {
  if (quarry === null) return "drop";
  return skillDistance(selfXMm, selfYMm, quarry) < rangeUnits ? "cast" : "follow";
}

/** `__CheckDashAffect` (`PythonPlayerSkill.cpp:381-384`): the caster carries a
 *  charge skill's own affect, the `AFF_TANHWAN_DASH` its first packet set
 *  (`char_skill.cpp:2476-2479`). While it is live a charge skill's cooldown is
 *  not checked (`PythonPlayerSkill.cpp:274-283`). */
export function dashing(affects: readonly { readonly source: number }[]): boolean {
  return affects.some((affect) => isChargeSkill(affect.source));
}

/** `IsToggleSkill` (`PythonSkill.cpp:1128-1131`). */
export function isToggleSkill(vnum: number): boolean {
  return has(vnum, "TOGGLE");
}

/** `m_kMap_dwAffectIndexToSkillIndex` (`PythonPlayer.cpp:1670-1688`): the skill
 *  each `CInstanceBase` affect index belongs to. */
export const AFFECT_INDEX_TO_SKILL: ReadonlyMap<number, number> = new Map([
  [14, 3], [15, 4], [16, 19], [17, 49], [18, 34], [20, 64], [21, 65], [22, 94],
  [23, 95], [24, 110], [29, 96], [30, 111], [32, 66], [33, 19],
  [19, 63], [26, 78], [25, 79],
]);

/** The toggle skills whose slots are lit for a character under `affectFlags`:
 *  `CPythonPlayer::SetAffect` maps each set affect index to its skill and
 *  activates that skill's slot only when it is a toggle
 *  (`PythonPlayerSkill.cpp:15-37`). */
export function activeToggleSkills(affectFlags: number): Set<number> {
  const active = new Set<number>();
  for (const [index, vnum] of AFFECT_INDEX_TO_SKILL) {
    if (hasAffect(affectFlags, index) && isToggleSkill(vnum)) active.add(vnum);
  }
  return active;
}
